import React, { useEffect, useRef, useState } from 'react';

import { useDebouncedValue } from '@mantine/hooks';
import Select, {
  components,
  DropdownIndicatorProps,
  OnChangeValue,
  OptionProps,
  Props,
  StylesConfig
} from 'react-select';

import { useGetDiagnosisCodesQuery } from '@/store/slices/api/diagnosisCodesApiSlice';
import InputLabel from '@/components/InputLabel';
import { plum } from '@/styles/colors';

import { ReportUpdateFieldKey } from '@/types/transmissionReport';

export interface LabelAndValue {
  label: string;
  value: string;
}

/** Props for the DiagnosisSelect component. */
interface CustomSelectProps extends Props<LabelAndValue, false> {
  category: ReportUpdateFieldKey;
  label?: string;
  name?: string;
  defaultValue?: LabelAndValue;
}

/**
 * Highlights the search term within the provided text.
 */
function highlightSearchTerm(text?: string, keyword?: string): React.ReactNode {
  if (!text || !keyword) return text;
  const lowerKeyword = keyword.toLowerCase();
  const index = text.toLowerCase().indexOf(lowerKeyword);
  if (index === -1) return text;
  const before = text.slice(0, index);
  const match = text.slice(index, index + keyword.length);
  const after = text.slice(index + keyword.length);
  return (
    <span>
      {before}
      <b style={{ color: plum[6] }}>{match}</b>
      {after}
    </span>
  );
}

/**
 * Custom option component that highlights the matched search term.
 */
const HighlightedOption = (props: OptionProps<LabelAndValue, false>) => {
  const { inputValue } = props.selectProps;
  return (
    <components.Option {...props}>
      {highlightSearchTerm(props.data.label, inputValue)}
    </components.Option>
  );
};

/**
 * Custom dropdown indicator using a custom SVG with plum color.
 */
const CustomDropdownIndicator = (props: DropdownIndicatorProps<LabelAndValue, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path
          d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
          fill={plum[6]}></path>
      </svg>
    </components.DropdownIndicator>
  );
};

/**
 * Custom styles for the react-select component with plum theming.
 */
const selectStyles: StylesConfig<LabelAndValue, false> = {
  control: (styles, { isFocused }) => ({
    ...styles,
    minHeight: '35px',
    height: '35px',
    cursor: 'pointer',
    borderColor: isFocused ? plum[6] : plum[3],
    boxShadow: isFocused ? `0 0 0 1px ${plum[6]}` : 'none',
    fontSize: '14px',
    '&:hover': {
      borderColor: plum[5]
    }
  }),
  singleValue: (styles) => ({
    ...styles,
    fontSize: '14px',
    color: plum[7]
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    height: '18px',
    backgroundColor: plum[3]
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: '35px'
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: '4px',
    border: `1px solid ${plum[3]}`
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    backgroundColor: isSelected ? plum[6] : isFocused ? plum[2] : 'transparent',
    color: isSelected ? 'white' : plum[8],
    fontSize: '14px',
    '&:hover': {
      backgroundColor: isSelected ? plum[6] : plum[2]
    }
  }),
  input: (styles) => ({
    ...styles,
    color: plum[8]
  }),
  placeholder: (styles) => ({
    ...styles,
    color: plum[4]
  }),
  dropdownIndicator: (styles, { isFocused }) => ({
    ...styles,
    color: isFocused ? plum[6] : plum[4],
    '&:hover': {
      color: plum[5]
    }
  }),
  clearIndicator: (styles) => ({
    ...styles,
    color: plum[4],
    '&:hover': {
      color: plum[6]
    }
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: plum[2]
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: plum[7]
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: plum[5],
    '&:hover': {
      backgroundColor: plum[5],
      color: 'white'
    }
  })
};

/**
 * DiagnosisSelect: A searchable, controlled select component that fetches diagnosis codes.
 * The selection handling is now driven by the parent component via the onChange prop.
 * This version uses a plum color scheme.
 */
function DiagnosisSelect(props: CustomSelectProps) {
  const { defaultValue, name, label, onChange, ...rest } = props;

  // Local state for the search input and selected option.
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm] = useDebouncedValue(searchInput, 300);
  const [selectedOption, setSelectedOption] = useState<LabelAndValue | null>(defaultValue || null);
  const isFirstFocus = useRef(true); // Tracks if the input is focused for the first time
  const [lastSelectedOption, setLastSelectedOption] = useState<LabelAndValue | null>(
    defaultValue || null
  );

  useEffect(() => {
    setSelectedOption(defaultValue || null);
  }, [defaultValue]);

  // Fetch diagnosis codes based on the debounced search term.
  const { data: diagnosisCodesResponse, isLoading } = useGetDiagnosisCodesQuery({
    query: debouncedSearchTerm
  });

  const handleFocus = () => {
    if (isFirstFocus.current) {
      setSelectedOption(null); // Clear the selected value on first focus
      isFirstFocus.current = false; // Set to false after the first focus
    }
  };
  // Build options list from fetched diagnosis codes.
  const fetchedOptions: LabelAndValue[] =
    diagnosisCodesResponse?.map(({ code, description }) => ({
      value: code,
      label: `${code} - ${description}`
    })) || [];

  const filteredOptions = fetchedOptions.filter(
    (option) => option.value !== lastSelectedOption?.value
  );
  // Prepend a default empty option.
  const allOptions: LabelAndValue[] = [
    { label: '-', value: '' },
    ...(lastSelectedOption ? [lastSelectedOption] : []),
    ...filteredOptions
  ];
  /**
   * Handles the change event of the select.
   * Now, it simply updates the local state and calls the parent's onChange handler (if provided).
   */
  const handleOnChange = (option: OnChangeValue<LabelAndValue, false>, actionMeta: any) => {
    setSelectedOption(option);
    setLastSelectedOption(option);
    if (onChange) {
      onChange(option, actionMeta);
    }
  };

  return (
    <>
      {label && <InputLabel label={label} name={name || ''} />}
      <Select<LabelAndValue, false>
        menuPortalTarget={document.body}
        onChange={handleOnChange}
        value={selectedOption}
        onInputChange={(input) => setSearchInput(input)}
        isSearchable
        onFocus={handleFocus}
        name={name}
        options={allOptions}
        isLoading={isLoading || rest.isLoading}
        components={{
          Option: HighlightedOption,
          DropdownIndicator: CustomDropdownIndicator
        }}
        styles={selectStyles}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: plum[6],
            primary75: plum[5],
            primary50: plum[4],
            primary25: plum[2]
          }
        })}
        {...rest}
      />
    </>
  );
}

export default DiagnosisSelect;
